import { Box, Flex, Divider } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

import { contentMargin } from 'theme/space'
import useSaveProgress, {
  SaveProgressParams,
} from 'lib/hooks/api/useSaveProgress'
import { Heading } from 'atoms/typography'
import Title from 'components/Title'
import LayoutContainer from 'components/LayoutContainer'
import NavBar, { NavBarProps } from 'components/NavBar'
import Footer from 'components/Footer'
import { StreetViewStopProps } from 'components/StreetView'
import StreetViewAccordion from 'components/StreetViewAccordion'
import Markdown from 'components/Markdown'
import Image from 'components/Image'
import ContinueNav, { ContinueNavProps } from 'components/ContinueNav'
import Auth from 'components/Auth'

export interface StopOverviewProps {
  title: string
  description: string
  imageUrl: string
  navBarProps: NavBarProps
  streetViewProps: StreetViewStopProps | null
  continueHref: ContinueNavProps['continueHref'] | null
  mapHref: ContinueNavProps['mapHref'] | null
  walkSlug: string
  saveProgressParams: SaveProgressParams
}

const StopOverview: React.FC<StopOverviewProps> = ({
  title,
  description,
  imageUrl,
  navBarProps,
  streetViewProps,
  continueHref,
  mapHref,
  walkSlug,
  saveProgressParams,
}) => {
  const { t } = useTranslation()

  useSaveProgress(saveProgressParams)

  return (
    <>
      <Title>{title}</Title>

      <Auth walkSlug={walkSlug} />

      <NavBar {...navBarProps} />

      {streetViewProps ? (
        <StreetViewAccordion
          pov={streetViewProps}
          stopTitle={title}
          caption={streetViewProps.caption ?? undefined}
        />
      ) : null}

      <LayoutContainer>
        <Box w={{ base: '100%', lg: '83%' }}>
          <Box display={{ base: 'none', sm: 'block', md: 'none' }}>
            <Heading size="xl">{title}</Heading>
            <Divider my={contentMargin.lg} aria-hidden />
          </Box>

          <Flex flexDirection={{ base: 'column-reverse', sm: 'row' }}>
            <Box
              w={{ base: '100%', sm: '60%', md: '50%' }}
              mt={{ base: '4', sm: '0' }}
            >
              <Image url={imageUrl} h={{ base: '88', sm: '96' }} />
            </Box>

            <Box
              w={{ base: '100%', sm: '40%', md: '50%' }}
              ml={{ base: '0', sm: '4', lg: '6' }}
            >
              <Box display={{ base: 'block', sm: 'none', md: 'block' }}>
                <Heading size="xl">{title}</Heading>
                <Divider my={contentMargin.md} />
              </Box>

              <Box as="article">
                <Markdown>{description}</Markdown>
              </Box>
            </Box>
          </Flex>

          <Divider my={contentMargin.lg} aria-hidden />
        </Box>

        <ContinueNav
          continueHref={continueHref ?? undefined}
          mapHref={mapHref ?? undefined}
          continueText={t('button.continue')}
          mapText={t('button.backToMap')}
        />
      </LayoutContainer>

      <Footer />
    </>
  )
}

export default StopOverview
